import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@workspace/ui/components/alert';
import { Button } from '@workspace/ui/components/button';
import { Checkbox } from '@workspace/ui/components/checkbox';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@workspace/ui/components/drawer';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import { toast } from '@workspace/ui/components/sonner';
import { useIsMobile } from '@workspace/ui/hooks/use-mobile';
import { AlertTriangle, CheckCircle2, Clock, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { m } from '~/paraglide/messages.js';

interface CreateBusinessApiKeyFormData {
  name: string;
  expiration?: string;
  permissions: string[];
}

const BUSINESS_PERMISSIONS = [
  { value: 'people.read', labelKey: 'people_read' },
  { value: 'people.write', labelKey: 'people_write' },
  { value: 'payments.read', labelKey: 'payments_read' },
  { value: 'payments.write', labelKey: 'payments_write' },
  { value: 'payment_links.read', labelKey: 'payment_links_read' },
  { value: 'payment_links.write', labelKey: 'payment_links_write' },
  { value: 'orgs.read', labelKey: 'orgs_read' },
  { value: 'orgs.write', labelKey: 'orgs_write' },
] as const;

export function CreateBusinessApiKeyDrawer() {
  const [open, setOpen] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const isMobile = useIsMobile();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateBusinessApiKeyFormData>({
    defaultValues: {
      permissions: [],
      expiration: '',
    },
  });

  const permissions = watch('permissions');

  const onSubmit = async (data: CreateBusinessApiKeyFormData) => {
    if (data.permissions.length === 0) {
      toast.error(
        m['developer.business_api_keys.create_drawer.permissions_required'](),
      );
      return;
    }
    setRequestSubmitted(true);
    toast.success(m['developer.business_api_keys.toast.request_submitted']());
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setRequestSubmitted(false);
      reset();
    }, 300);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
        else setOpen(true);
      }}
      direction={isMobile ? 'bottom' : 'right'}
    >
      <DrawerTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          {m['developer.business_api_keys.create_drawer.title']()}
        </Button>
      </DrawerTrigger>
      <DrawerContent
        className={
          isMobile
            ? 'mx-auto flex max-h-[90vh] max-w-2xl flex-col'
            : '!w-[40%] !max-w-none'
        }
      >
        <DrawerHeader className="flex-shrink-0">
          <DrawerTitle>
            {m['developer.business_api_keys.create_drawer.title']()}
          </DrawerTitle>
          <DrawerDescription>
            {m['developer.business_api_keys.create_drawer.subtitle']()}
          </DrawerDescription>
        </DrawerHeader>

        {requestSubmitted ? (
          <div className="flex flex-1 flex-col gap-4 p-4">
            <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/20">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle className="text-green-800 dark:text-green-400">
                {m['developer.business_api_keys.request_submitted.title']()}
              </AlertTitle>
              <AlertDescription className="text-green-700 dark:text-green-300">
                {m['developer.business_api_keys.request_submitted.message']()}
              </AlertDescription>
            </Alert>

            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-3">
              <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {m[
                  'developer.business_api_keys.request_submitted.pending_note'
                ]()}
              </p>
            </div>

            <div className="flex-1" />

            <Button onClick={handleClose} className="w-full">
              {m['common.done']()}
            </Button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={
              isMobile ? 'flex min-h-0 flex-1 flex-col' : 'flex h-full flex-col'
            }
          >
            <div className="flex-1 space-y-6 overflow-y-auto px-4">
              <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-amber-700 dark:text-amber-300 text-xs">
                  {m[
                    'developer.business_api_keys.create_drawer.request_notice'
                  ]()}
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="name">
                  {m['developer.business_api_keys.create_drawer.name_label']()}
                </Label>
                <Input
                  id="name"
                  {...register('name', {
                    required:
                      m[
                        'developer.api_keys.create_drawer.validation.name_required'
                      ](),
                  })}
                  placeholder={m[
                    'developer.business_api_keys.create_drawer.name_placeholder'
                  ]()}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>
                  {m[
                    'developer.business_api_keys.create_drawer.permissions_label'
                  ]()}
                </Label>
                <div className="grid gap-2 overflow-y-auto rounded-md border p-4">
                  <p className="mb-2 text-xs text-muted-foreground">
                    {m[
                      'developer.business_api_keys.create_drawer.permissions_description'
                    ]()}
                  </p>
                  {BUSINESS_PERMISSIONS.map(({ value, labelKey }) => (
                    <div key={value} className="flex items-center space-x-2">
                      <Checkbox
                        id={value}
                        checked={permissions?.includes(value)}
                        onCheckedChange={(checked) => {
                          const current = permissions || [];
                          if (checked) {
                            setValue('permissions', [...current, value]);
                          } else {
                            setValue(
                              'permissions',
                              current.filter((s) => s !== value),
                            );
                          }
                        }}
                      />
                      <label
                        htmlFor={value}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {m[
                          `developer.business_api_keys.create_drawer.permissions.${labelKey}`
                        ]()}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiration">
                  {m[
                    'developer.business_api_keys.create_drawer.expiration_label'
                  ]()}
                </Label>
                <Input
                  id="expiration"
                  type="date"
                  {...register('expiration')}
                  style={{ pointerEvents: 'auto' }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                />
                <p className="text-sm text-muted-foreground">
                  {m[
                    'developer.business_api_keys.create_drawer.expiration_description'
                  ]()}
                </p>
              </div>
            </div>

            <DrawerFooter className="mt-auto flex-shrink-0 border-t px-4 py-4">
              <Button type="submit" className="w-full">
                {m[
                  'developer.business_api_keys.create_drawer.request_button'
                ]()}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" type="button">
                  {m['common.cancel']()}
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </form>
        )}
      </DrawerContent>
    </Drawer>
  );
}
